import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { type SiteSummary, listSites } from '../../lib/sites';
import {
  AppShellContext,
  type AppShellContextValue,
  type SidebarActions,
} from './app-shell-context';
import AppSidebar from './app-sidebar';

function getSiteOrderDate(site: SiteSummary) {
  return site.updated_at ?? site.created_at;
}

function sortSitesByLatest(sites: SiteSummary[]) {
  return [...sites].sort((left, right) => getSiteOrderDate(right).localeCompare(getSiteOrderDate(left)));
}

export default function AppShell() {
  const { siteId } = useParams<{ siteId?: string }>();
  const location = useLocation();
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState('');
  const [sidebarActions, setSidebarActionsState] = useState<SidebarActions>({});

  const refreshSites = useCallback(async () => {
    setSitesLoading(true);
    setSitesError('');

    try {
      const nextSites = await listSites();
      setSites(nextSites);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'サイト一覧の取得に失敗しました。';
      setSitesError(message);
    } finally {
      setSitesLoading(false);
    }
  }, []);

  const upsertSite = useCallback((site: SiteSummary) => {
    setSites((prev) => {
      const nextSites = prev.filter((item) => item.id !== site.id);
      nextSites.push(site);
      return sortSitesByLatest(nextSites);
    });
    setSitesError('');
  }, []);

  const removeSite = useCallback((siteIdToRemove: string) => {
    setSites((prev) => prev.filter((site) => site.id !== siteIdToRemove));
    setSitesError('');
  }, []);

  const setSidebarActions = useCallback((actions: SidebarActions) => {
    setSidebarActionsState((prev) => {
      if (prev.onUndo === actions.onUndo && prev.onToggleHistory === actions.onToggleHistory) {
        return prev;
      }

      return actions;
    });
  }, []);

  const clearSidebarActions = useCallback(() => {
    setSidebarActionsState((prev) => {
      if (!prev.onUndo && !prev.onToggleHistory) {
        return prev;
      }

      return {};
    });
  }, []);

  useEffect(() => {
    void refreshSites();
  }, [refreshSites]);

  const contextValue = useMemo<AppShellContextValue>(() => ({
    sites,
    sitesLoading,
    sitesError,
    refreshSites,
    upsertSite,
    removeSite,
    setSidebarActions,
    clearSidebarActions,
  }), [clearSidebarActions, refreshSites, removeSite, setSidebarActions, sites, sitesError, sitesLoading, upsertSite]);
  const isEditorRoute = location.pathname.startsWith('/app/editor/');
  const isTemplateCatalogRoute = location.pathname.startsWith('/app/template-catalog');
  const isTemplatesRoute = location.pathname.startsWith('/app/templates');
  const mainClassName = isEditorRoute
    ? 'flex-1 bg-surfaceMuted px-6 py-8 md:h-dvh md:overflow-y-auto md:px-10 md:py-10'
    : 'flex-1 px-6 py-8 md:h-dvh md:overflow-y-auto md:px-10 md:py-10';
  const contentClassName = isEditorRoute || isTemplateCatalogRoute || isTemplatesRoute
    ? 'w-full'
    : 'mx-auto w-full max-w-6xl';

  return (
    <AppShellContext.Provider value={contextValue}>
      <div className="app-page">
        <div className="min-h-screen flex flex-col md:h-dvh md:flex-row md:overflow-hidden">
          <AppSidebar
            siteId={siteId}
            onUndo={sidebarActions.onUndo}
            onToggleHistory={sidebarActions.onToggleHistory}
          />
          <main className={mainClassName}>
            <div className={contentClassName}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </AppShellContext.Provider>
  );
}
